import React, { useState, useEffect } from 'react';
import API_ROUTES from './config/apiConfig';
import { getPublicRequest } from '../api/auth';
import logoImg from '../assets/logo.png';

export default function Footer({ onNavigate }) {
  const [openSection, setOpenSection] = useState(null);
  const [categories, setCategories] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getPublicRequest(API_ROUTES.GET_CATEGORIES);

        if (res.success) {
          const activeCategories = (res.data || []).filter(
            item => item.isActive
          );

          setCategories(activeCategories);
        }

      } catch (err) {
        console.log(err);
      }
    };

    fetchCategories();

  }, []);

  const isMobile = windowWidth <= 768;

  const toggleSection = (idx) => {
    if (isMobile) setOpenSection(openSection === idx ? null : idx);
  };

  const shouldShow = (idx) => {
    if (!isMobile) return true;
    return openSection === idx;
  };

  return (
    <footer className="main-footer">
      <div className="container footer-grid">

        {/* FIRST COLUMN: Brand Identity Presentation */}
        <div className="footer-col brand-summary-node">
          <div
            className="footer-logo-container"
            onClick={() => onNavigate('home')}
          >
            <img src={logoImg} alt="Lightingale Creative Footer Logo" className="footer-logo" />
          </div>
          <p className="footer-brand-prose">
            Timeless elegance in every lamp.<br />
            Handcrafted with passion,<br />
            designed for perfection.
          </p>

          {/* SOCIAL BRAND CHANNELS STRIPE */}
          <div className="footer-social-deck-row">
            <a href="#facebook" aria-label="Facebook">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
            </a>
            <a href="#instagram" aria-label="Instagram">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
            </a>
            <a href="#linkedin" aria-label="LinkedIn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>
            </a>
            <a href="#youtube" aria-label="YouTube">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" /><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" /></svg>
            </a>
          </div>
        </div>

        {/* SECOND COLUMN: Quick Collections Links */}
        <div className={`footer-col ${openSection === 1 ? 'active-accordion-node' : ''}`}>
          <div className="footer-accordion-header" onClick={() => toggleSection(1)}>
            <h4>Quick Links</h4>
            <span>{openSection === 1 ? '−' : '+'}</span>
          </div>
          {shouldShow(1) && (
            <ul className="footer-accordion-content">
              <li><a href="#about" onClick={(e) => { e.preventDefault(); onNavigate('about'); }}>About Us</a></li>
              {categories.map((category) => (
                <li key={category._id}>
                  <a
                    href={`/category/${category.slug}`}
                    onClick={(e) => {
                      e.preventDefault();

                      onNavigate("subcategory", {
                        slug: category.slug
                      });

                    }}
                  >
                    {category.name}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* THIRD COLUMN: B2B Procurement Services */}
        <div className={`footer-col ${openSection === 2 ? 'active-accordion-node' : ''}`}>
          <div className="footer-accordion-header" onClick={() => toggleSection(2)}>
            <h4>B2B Services</h4>
            <span>{openSection === 2 ? '−' : '+'}</span>
          </div>
          {shouldShow(2) && (
            <ul className="footer-accordion-content">
              <li><a href="#custom-design" onClick={(e) => { e.preventDefault(); onNavigate('custom-design'); }}>Custom Design</a></li>
              {/* <li><a href="#new-arrivals" onClick={(e) => { e.preventDefault(); onNavigate('new-arrivals'); }}>New Arrivals</a></li> */}
              <li><a href="#contact" onClick={(e) => { e.preventDefault(); onNavigate('contact'); }}>Bulk Orders</a></li>
              <li><a href="#terms-conditions" onClick={(e) => { e.preventDefault(); onNavigate('terms'); }}>Terms & Conditions</a></li>
            </ul>
          )}
        </div>

        {/* FOURTH COLUMN: Support Desk options */}
        <div className={`footer-col ${openSection === 3 ? 'active-accordion-node' : ''}`}>
          <div className="footer-accordion-header" onClick={() => toggleSection(3)}>
            <h4>Support</h4>
            <span>{openSection === 3 ? '−' : '+'}</span>
          </div>
          {shouldShow(3) && (
            <ul className="footer-accordion-content">
              <li><a href="#faq" onClick={(e) => { e.preventDefault(); onNavigate('faq'); }}>FAQs</a></li>
              <li><a href="#privacy-policy" onClick={(e) => { e.preventDefault(); onNavigate('privacy'); }}>Privacy Policy</a></li>
              <li><a href="#contact" onClick={(e) => { e.preventDefault(); onNavigate('contact'); }}>Contact Us</a></li>
            </ul>
          )}
        </div>

        {/* FIFTH COLUMN: Direct Communication Desks */}
        <div className={`footer-col ${openSection === 4 ? 'active-accordion-node' : ''}`}>
          <div className="footer-accordion-header" onClick={() => toggleSection(4)}>
            <h4>Get In Touch</h4>
            <span>{openSection === 4 ? '−' : '+'}</span>
          </div>
          {shouldShow(4) && (
            <div className="footer-accordion-content contact-details-wrapper">

              {/* Phone Node item */}
              <div className="contact-line-node">
                <svg className="contact-node-vector" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                <a href="tel:+919350824043">+91 9350824043</a>
              </div>

              {/* Mail Node item */}
              <div className="contact-line-node">
                <svg className="contact-node-vector" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                <a href="mailto:bkksaj@gmail.com">bkksaj@gmail.com</a>
              </div>

              {/* Geographic Location item */}
              <div className="contact-line-node alignment-top">
                <svg className="contact-node-vector push-top" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                <p className="address-display-paragraph">
                  T-220, Gali No 01<br />
                  Gautampuri, Delhi-53<br />
                  Near ADGITM college
                </p>
              </div>

            </div>
          )}
        </div>
      </div>

      {/* CORE HORIZONTAL DIVIDER SPLIT */}
      <div className="container"><div className="footer-horizontal-divider-bar"></div></div>

      {/* FOOTER BOTTOM MATRICES ROW */}
      <div className="container footer-bottom">
        <div className="copyright-info-block">
          <p>© 2026 Lightingale Creative.</p>
          <p>All Rights Reserved.</p>
        </div>

        {/* PAYMENT CHANNELS SYMBOLS ROW */}
        <div className="payment-badges-row-deck">
          <span className="badge-item visa">VISA</span>
          <span className="badge-item mastercard">
            <span className="mc-circles-wrapper">
              <span className="circle-red"></span>
              <span className="circle-orange"></span>
            </span>
          </span>
          <span className="badge-item paypal">
            <span className="pp-italic-text">PayPal</span>
          </span>
          <span className="badge-item upi-gateway">
            <span className="upi-text-string">UPI</span>
            <span className="upi-accent-arrows">
              <span className="arrow-cyan"></span>
              <span className="arrow-orange"></span>
            </span>
          </span>
        </div>
      </div>
    </footer>
  );
}